<template>
  <v-container fluid class="pa-2">

    <!-- Control bar -->
    <v-layout row wrap align-center class="mb-2">
      <v-flex xs12 sm6>
        <h2 class="page-title">Tasting Event Request Form</h2>
        <div class="page-subtitle">Request tasting events &mdash; submitted for HSP approval</div>
      </v-flex>
      <v-flex xs12 sm6 class="text-xs-right">
        <v-btn class="downlink" flat :disabled="submitting || submissionUncertain" @click="resetForm">
          <v-icon left color="dark_grey">mdi-refresh</v-icon> Clear
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- ============ TOP HALF: Requester + Products ============ -->
    <v-card class="section-card mb-3">
      <div class="section-header">Submitted By</div>
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 sm6 md3 class="px-2">
            <v-select
              v-model="selectedRegion"
              :items="regions"
              item-text="text"
              item-value="value"
              label="Region"
              prepend-icon="mdi-map-marker"
              disabled
              :hint="requestProgram.name"
              persistent-hint
            />
          </v-flex>
          <v-flex xs12 sm6 md3 class="px-2">
            <v-text-field
              v-model="submittedBy.name"
              :required="!!groupContext"
              :error-messages="contactErrors.name"
              label="Name"
              @input="contactErrors.name = ''">
              <template slot="label">Name <span
                v-if="groupContext"
                class="required-mark">*</span></template>
            </v-text-field>
          </v-flex>
          <v-flex xs12 sm6 md3 class="px-2">
            <v-text-field v-model="submittedBy.organization" label="Supplier" />
          </v-flex>
          <v-flex xs12 sm6 md3 class="px-2">
            <v-text-field
              v-model="submittedBy.email"
              :required="!!groupContext"
              :error-messages="contactErrors.email"
              type="email"
              label="Email"
              prepend-icon="mdi-email-outline"
              @input="contactErrors.email = ''">
              <template slot="label">Email <span
                v-if="groupContext"
                class="required-mark">*</span></template>
            </v-text-field>
          </v-flex>
          <v-flex xs12 sm6 md3 class="px-2">
            <v-text-field
              v-model="submittedBy.phone"
              :required="!!groupContext"
              :error-messages="contactErrors.phone"
              type="tel"
              label="Phone"
              prepend-icon="mdi-phone-outline"
              @input="contactErrors.phone = ''">
              <template slot="label">Phone <span
                v-if="groupContext"
                class="required-mark">*</span></template>
            </v-text-field>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Products (with Substitutions section) -->
    <v-card class="section-card mb-3">
      <div class="section-header">Products</div>
      <v-card-text>
        <p class="slot-hint">
          <v-icon small color="dark_grey">mdi-information-outline</v-icon>
          {{ groupContext ? "Choose 1-3 products and add substitutions if available. HSP reviews each request before approval." : "Sample up to 3 products from the same brand per event; choose at least 1 substitution." }}
        </p>
        <div class="table-scroll-container-x">
          <table class="form-table">
            <thead>
              <tr>
                <th class="req">Brand</th>
                <th class="req">Item</th>
                <th>{{ requestProgram.cic_label }}</th>
                <th class="req">UPC</th>
                <th>Demo Co</th>
                <th>Regular Retail</th>
                <th>TPR Retail</th>
                <th class="col-action"></th>
              </tr>
            </thead>

            <!-- Products -->
            <tbody>
              <tr v-for="(row, i) in products" :key="'p' + i" :class="{ 'new-item-row': row.isNew }">
                <td><v-text-field v-model="row.supplier" single-line hide-details placeholder="Brand" /></td>
                <td class="item-col">
                  <v-text-field v-if="row.isNew" v-model="row.item" single-line hide-details placeholder="New item name" />
                  <async-autocomplete
                    v-else
                    ref="productAutos"
                    :value="row.selected"
                    :fetch="fetchProducts"
                    allow-create
                    placeholder="Search item..."
                    @selected="v => onProductSelect(row, v)"
                    @create="q => onNewItem(row, q)"
                  />
                </td>
                <td><span class="autofill">{{ row.cic || (row.isNew ? '(new)' : '—') }}</span></td>
                <td><v-text-field v-model="row.upc" single-line hide-details placeholder="UPC" /></td>
                <td><v-text-field v-model="row.demoCo" single-line hide-details /></td>
                <td><v-text-field v-model="row.regRetail" single-line hide-details prefix="$" /></td>
                <td><v-text-field v-model="row.tprRetail" single-line hide-details prefix="$" /></td>
                <td class="col-action">
                  <v-btn icon small flat @click="products.splice(i, 1)" :disabled="products.length === 1">
                    <v-icon small color="dark_grey">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="products.length < 3">
                <td colspan="8" class="add-row-cell">
                  <v-btn class="add-btn" flat small color="primary" @click="addProduct">
                    <v-icon left small>mdi-plus</v-icon> Add product
                  </v-btn>
                </td>
              </tr>
            </tbody>

            <!-- Substitutions -->
            <tbody>
              <tr class="subs-divider-row">
                <td colspan="8">Substitutions</td>
              </tr>
              <tr v-for="(row, i) in substitutions" :key="'s' + i" :class="{ 'new-item-row': row.isNew }">
                <td><v-text-field v-model="row.supplier" single-line hide-details placeholder="Brand" /></td>
                <td class="item-col">
                  <v-text-field v-if="row.isNew" v-model="row.item" single-line hide-details placeholder="New item name" />
                  <async-autocomplete
                    v-else
                    ref="subAutos"
                    :value="row.selected"
                    :fetch="fetchProducts"
                    allow-create
                    placeholder="Search item..."
                    @selected="v => onProductSelect(row, v)"
                    @create="q => onNewItem(row, q)"
                  />
                </td>
                <td><span class="autofill">{{ row.cic || (row.isNew ? '(new)' : '—') }}</span></td>
                <td><v-text-field v-model="row.upc" single-line hide-details placeholder="UPC" /></td>
                <td><v-text-field v-model="row.demoCo" single-line hide-details /></td>
                <td><v-text-field v-model="row.regRetail" single-line hide-details prefix="$" /></td>
                <td><v-text-field v-model="row.tprRetail" single-line hide-details prefix="$" /></td>
                <td class="col-action">
                  <v-btn icon small flat @click="substitutions.splice(i, 1)">
                    <v-icon small color="dark_grey">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="substitutions.length < 3">
                <td colspan="8" class="add-row-cell">
                  <v-btn class="add-btn" flat small color="primary" @click="addSubstitution">
                    <v-icon left small>mdi-plus</v-icon> Add substitution
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- ============ BOTTOM HALF: Store locations x day/time slots ============ -->
    <v-card class="section-card mb-3">
      <div class="section-header">Store Locations &amp; Requested Days</div>
      <v-card-text>
        <p class="slot-hint">
          <v-icon small color="dark_grey">mdi-information-outline</v-icon>
          <a v-if="!groupContext" href="/shared-calendar/sn" target="_blank" rel="noopener"><strong>Click here</strong></a>
          <a v-else href="#" @click.prevent="$emit('show-calendar')"><strong>Click here</strong></a> to view availability calendar.
          Pick a week for each store, then check the day(s) you're requesting. {{ requestProgram.slots ? "Choose a three-hour morning or afternoon slot. Unavailable times and dates inside the notice period are disabled." : "Times are fixed by day of week. Red dates have already been booked." }}
        </p>

        <div class="table-scroll-container-x">
          <table class="form-table store-slot-table">
            <thead>
              <tr>
                <th class="store-col">Store</th>
                <th class="store-col">City</th>
                <th class="store-col">Address</th>
                <th class="date-col">Week</th>
                <th
                  v-for="slot in dowSlots"
                  :key="slot.key"
                  class="slot-col"
                  :style="{ background: slot.color, color: '#fff' }"
                >
                  <div class="slot-day">{{ slot.label }}</div>
                  <div class="slot-time">{{ slot.time }}</div>
                </th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in storeRows" :key="'store' + i">
                <td class="store-col">
                  <async-autocomplete
                    :value="row.store"
                    :fetch="fetchStores"
                    placeholder="Search store #, city..."
                    @selected="v => onStoreSelect(row, v)"
                  />
                </td>
                <td
                  :class="{ 'store-location': groupContext }"
                  class="store-col"><span class="autofill">{{ row.city || '—' }}</span></td>
                <td
                  :class="{ 'store-location': groupContext }"
                  class="store-col"><span class="autofill">{{ row.address || '—' }}</span></td>
                <td class="date-col">
                  <span v-if="row.bookedLoading">Checking times...</span>
                  <button v-if="row.bookedError" type="button" @click="fetchBooked(row)">Availability unavailable. Retry</button>
                  <v-menu
                    v-model="row.dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    lazy
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      :value="weekLabel(row.weekStart)"
                      placeholder="Pick a week"
                      single-line
                      hide-details
                      readonly
                    />
                    <v-date-picker
                      :value="row.weekStart"
                      :min="requestProgram.earliest_date || undefined"
                      no-title
                      @input="d => onWeekPick(row, d)"
                    />
                  </v-menu>
                </td>
                <td
                  v-for="(slot, si) in dowSlots"
                  :key="slot.key"
                  class="slot-col slot-checkbox-cell"
                >
                  <v-checkbox
                    v-model="row.slots[slot.key]"
                    :disabled="isSlotBooked(row, si)"
                    :aria-label="slot.label + ' ' + slot.time + ' ' + slotDate(row.weekStart, si)"
                    hide-details
                    color="#495AD4"
                    class="slot-checkbox"
                  />
                  <div v-if="row.weekStart" class="slot-date" :class="{ 'slot-date-booked': isSlotBooked(row, si) }">{{ slotDate(row.weekStart, si) }}</div>
                </td>
                <td class="col-action">
                  <v-btn icon small flat @click="storeRows.splice(i, 1)" :disabled="storeRows.length === 1">
                    <v-icon small color="dark_grey">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <v-btn class="add-btn mt-2" flat small color="primary" @click="addStoreRow">
          <v-icon left small>mdi-plus</v-icon> Add store
        </v-btn>
      </v-card-text>
    </v-card>

    <v-layout row wrap class="mb-4">
      <v-spacer />
      <v-btn color="tertiary" dark large :loading="submitting" :disabled="submitting" @click="submit">
        <v-icon left>mdi-send</v-icon> Submit Request
      </v-btn>
    </v-layout>

    <v-snackbar v-model="submitted" :timeout="6000" :color="submitOk ? 'yay' : 'oops'" bottom>
      {{ submitMessage }}
    </v-snackbar>

    <!-- Persistent success dialog: must be dismissed, so the submitted form
         cannot be accidentally resubmitted -->
    <v-dialog v-model="showSuccess" persistent max-width="600">
      <v-card class="success-card">
        <div class="success-header">
          <v-icon class="success-icon">mdi-check-circle-outline</v-icon>
          <h3 class="success-title">Request submitted</h3>
        </div>
        <v-card-text class="text-xs-center px-4 py-4">
          <p class="success-body">Thank you for submitting your tasting event requests! Our team will review your requested events and follow up by email soon.</p>
          <p v-if="requestReference" class="success-reference">
            {{ groupContext ? 'Work order reference' : 'Reference' }}: <strong>{{ requestReference }}</strong>
          </p>
          <p
            v-if="groupContext && requestReference"
            class="success-body">This reference identifies the events you submitted together. Include it when contacting <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a> about this request. Your events are pending HSP approval.</p>
        </v-card-text>
        <v-card-actions class="pb-4">
          <v-spacer />
          <v-btn color="primary" dark large @click="dismissSuccess">Done</v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
// EventRequest.vue - client-side event request form.
// Product/store fields are backed by live autocomplete endpoints on
// ClientController; Submit POSTs to ClientController::createEventRequest.
import axios from 'axios'
import { TokenService } from '../services/storage.service'
import AsyncAutocomplete from '@/components/AsyncAutocomplete'
import { GROUP_CONTACT_EMAIL } from '../services/group.service'

export default {
  components: { AsyncAutocomplete },

  props: {
    groupContext: { type: Object, default: null }
  },

  computed: {
    contactEmail () {
      return this.groupContext ? GROUP_CONTACT_EMAIL : 'events@hspbrands.com'
    },
    requestProgram () {
      return this.groupContext ? this.groupContext.program : {
        region: 'cano', region_name: 'CA - NorCal', name: 'ABSCO NorCal', cic_label: 'NorCal SFWY CIC'
      }
    },
    dowSlots () {
      if (!this.requestProgram.slots) return this.legacySlots
      return this.legacySlots.reduce((slots, day, offset) => slots.concat(this.requestProgram.slots.map(time => ({
        key: `${day.key}_${time.key}`, label: day.label, time: time.label,
        color: day.color, offset, start: time.start, end: time.end
      }))), [])
    },
    requestToken () {
      return this.groupContext ? this.groupContext.token : TokenService.getAccessKey()
    },
    requestBase () {
      return this.groupContext
        ? `groups/${this.groupContext.code}/suppliers/${this.groupContext.supplier.client_code}/`
        : `clients/${TokenService.getLoginCode()}/`
    }
  },

  data: () => ({
    submittedBy: { name: '', organization: '', email: '', phone: '' },
    contactErrors: { name: '', email: '', phone: '' },
    products: [],
    substitutions: [],
    storeRows: [],
    submitted: false,
    submitting: false,
    submitOk: true,
    submitMessage: '',
    showSuccess: false,
    requestReference: '',
    idempotencyKey: '',
    submissionUncertain: false,
    retryPayload: null,

    // Mirrors $cfg['regions'] in the portal. Locked to NorCal ('cano') for v1
    // so only NorCal stores are offered; the select is present for future
    // multi-region support.
    selectedRegion: 'cano',
    regions: [
      { value: 'cano', text: 'CA - NorCal' }
    ],

    // Day-of-week slots with fixed times. Subtle brand-blue gradient across the
    // week (light periwinkle → royal), kept low-key so the grid reads instantly.
    legacySlots: [
      { key: 'mon', label: 'Mon', time: '2–6pm',  color: '#818CD0' },
      { key: 'tue', label: 'Tue', time: '2–6pm',  color: '#6E76C8' },
      { key: 'wed', label: 'Wed', time: '2–6pm',  color: '#5B5FC0' },
      { key: 'thu', label: 'Thu', time: '2–6pm',  color: '#4849B8' },
      { key: 'fri', label: 'Fri', time: '2–6pm',  color: '#3633AF' },
      { key: 'sat', label: 'Sat', time: '12–4pm', color: '#231CA7' },
      { key: 'sun', label: 'Sun', time: '12–4pm', color: '#10069F' }
    ]
  }),

  created () {
    this.selectedRegion = this.requestProgram.region
    this.regions = [{ value: this.requestProgram.region, text: this.requestProgram.region_name }]
    // Seed one empty row in each repeatable section
    this.idempotencyKey = this.createIdempotencyKey()
    this.prefillSubmitter()
    this.addProduct()
    this.addStoreRow()
  },

  watch: {
    products: { deep: true, handler () { this.emitDraftState() } },
    substitutions: { deep: true, handler () { this.emitDraftState() } },
    storeRows: { deep: true, handler () { this.emitDraftState() } },
    submitting () { this.emitDraftState() },
    submissionUncertain () { this.emitDraftState() }
  },

  methods: {
    emitDraftState () {
      const hasProducts = this.products.concat(this.substitutions).some(row => row.item || row.cp_wine_id)
      const hasStores = this.storeRows.some(row => row.costco_id || row.weekStart)
      this.$emit('draft-change', this.submitting || this.submissionUncertain || hasProducts || hasStores)
    },
    createIdempotencyKey () {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID()
      }

      const random = Math.random().toString(36).substring(2)
      return `event-${Date.now().toString(36)}-${random}`
    },

    prefillSubmitter () {
      const profile = this.groupContext ? {} : (this.$store.state.userProfile || TokenService.getUserProfile() || {})
      this.submittedBy = {
        name: profile.display_name || '',
        organization: this.groupContext ? this.groupContext.supplier.client_name : (profile.client_name || this.$store.state.company_name || ''),
        email: profile.email || '',
        phone: profile.phone || ''
      }
    },

    // Builds a GET against the client event-request endpoints, carrying the
    // client_code + access_key the dashboard already holds for this session.
    async apiGet (path, params) {
      await this.$store.dispatch('get_config')
      const qs = new URLSearchParams(params)
      return axios.get(`${this.$store.state.apiRoot}${this.requestBase}${path}?${qs.toString()}`, {
        headers: {
          Authorization: `Bearer ${this.requestToken}`
        }
      })
    },

    emptyProduct () {
      return {
        selected: null,
        supplier: '',
        item: '',
        cic: '',
        upc: '',
        demoCo: '',
        regRetail: '',
        tprRetail: '',
        cp_wine_id: null,
        producer_id: null,
        isNew: false
      }
    },

    emptyStoreRow () {
      return {
        store: null,
        city: '',
        address: '',
        costco_id: null,
        weekStart: '',
        dateMenu: false,
        bookedDates: [],
        slots: this.dowSlots.reduce((slots, slot) => Object.assign(slots, { [slot.key]: false }), {}),
        bookedLoading: false,
        bookedError: false,
        bookedVersion: 0
      }
    },

    addProduct () {
      if (this.products.length >= 3) return
      this.products.push(this.emptyProduct())
      // Drop the cursor straight into the new row's item field
      this.$nextTick(() => {
        const autos = this.$refs.productAutos
        if (autos && autos.length) autos[autos.length - 1].focus()
      })
    },

    addSubstitution () {
      if (this.substitutions.length >= 3) return
      this.substitutions.push(this.emptyProduct())
      this.$nextTick(() => {
        const autos = this.$refs.subAutos
        if (autos && autos.length) autos[autos.length - 1].focus()
      })
    },

    addStoreRow () {
      this.storeRows.push(this.emptyStoreRow())
    },

    // Product lookup (products and substitutions share it since both draw from
    // the client's catalog). Passed to AsyncAutocomplete as its fetch function.
    fetchProducts (query) {
      return this.apiGet('event-request/products', { q: query }).then(res => res.data || [])
    },

    onProductSelect (row, product) {
      row.selected = product
      if (product) {
        row.item = product.item
        row.supplier = product.producer_name
        row.upc = product.upc || ''
        row.cic = product.cic || ''
        row.cp_wine_id = product.id
        row.producer_id = product.producer_id
      } else {
        row.item = ''
        row.supplier = ''
        row.upc = ''
        row.cic = ''
        row.cp_wine_id = null
        row.producer_id = null
      }
    },

    // "Add '<typed>' as a new item" chosen: flip the row to manual entry with
    // the typed text as the item name. Brand + UPC become required (checked on
    // submit); CIC stays blank (HSP assigns it when the product is created). New
    // items are NOT written to cp_schedule - they only ride along in the admin
    // email so the team adds them to the catalog before reviewing the shifts.
    onNewItem (row, query) {
      row.isNew = true
      row.item = query || ''
      row.selected = null
      row.cp_wine_id = null
      row.producer_id = null
      row.cic = ''
    },

    // Store lookup (NorCal only, enforced server-side). Passed to
    // AsyncAutocomplete as its fetch function.
    fetchStores (query) {
      return this.apiGet('event-request/stores', { q: query }).then(res => res.data || [])
    },

    onStoreSelect (row, store) {
      row.store = store
      if (store) {
        row.city = store.city
        row.address = store.address
        row.costco_id = store.id
      } else {
        row.city = ''
        row.address = ''
        row.costco_id = null
      }
      this.fetchBooked(row)
    },

    // Snap any picked date to the Monday of its week and store that as the week start
    onWeekPick (row, date) {
      row.weekStart = this.mondayOf(date)
      row.dateMenu = false
      this.fetchBooked(row)
    },

    // Full YYYY-MM-DD date for a day-of-week offset (0=Mon..6=Sun) within the row's week
    slotIso (weekStart, offset) {
      const d = new Date(weekStart + 'T00:00:00')
      d.setDate(d.getDate() + offset)
      return d.toLocaleDateString('sv')
    },

    // Fetch the dates already booked at this store for the picked week, so those
    // day checkboxes get disabled. Uses the same busy rule as the shared calendar.
    fetchBooked (row) {
      const version = ++row.bookedVersion
      row.bookedDates = []
      row.bookedError = false
      if (!row.costco_id || !row.weekStart) {
        row.bookedLoading = false
        return
      }
      row.bookedLoading = true
      this.apiGet('event-request/booked', {
        store_id: row.costco_id,
        date_start: row.weekStart,
        date_end: this.slotIso(row.weekStart, 6)
      })
        .then(res => {
          if (version !== row.bookedVersion) return
          row.bookedLoading = false
          row.bookedDates = (res.data && res.data.booked) || []
          this.dowSlots.forEach((slot, si) => {
            if (this.isSlotBooked(row, si)) row.slots[slot.key] = false
          })
        })
        .catch(() => {
          if (version !== row.bookedVersion) return
          row.bookedLoading = false
          row.bookedError = true
        })
    },

    isSlotBooked (row, index) {
      if (!row.weekStart || !row.costco_id || row.bookedLoading || row.bookedError) return true
      const slot = this.dowSlots[index]
      const date = this.slotIso(row.weekStart, slot.offset === undefined ? index : slot.offset)
      if (this.requestProgram.earliest_date && date < this.requestProgram.earliest_date) return true
      return (row.bookedDates || []).some(booked => {
        if (typeof booked === 'string') return booked === date
        if (booked.schedule_date !== date) return false
        return !booked.time_start || !booked.time_end || booked.time_end <= booked.time_start ||
          (booked.time_start < slot.end && booked.time_end > slot.start)
      })
    },

    // Returns the YYYY-MM-DD of the Monday of the week containing the given date
    mondayOf (date) {
      const d = new Date(date + 'T00:00:00')
      const day = d.getDay()
      const diff = day === 0 ? -6 : 1 - day
      d.setDate(d.getDate() + diff)
      return d.toLocaleDateString('sv')
    },

    // Human label for a week, e.g. "Wk of Jul 6 - Jul 12"
    weekLabel (weekStart) {
      if (!weekStart) return ''
      const start = new Date(weekStart + 'T00:00:00')
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      const opts = { month: 'short', day: 'numeric' }
      return `Wk of ${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}`
    },

    // Concrete M/D date for a given day-of-week column (offset 0=Mon..6=Sun)
    // within the row's picked week. Drives the small date label under each box.
    slotDate (weekStart, offset) {
      if (!weekStart) return ''
      const d = new Date(weekStart + 'T00:00:00')
      const slot = this.dowSlots[offset]
      d.setDate(d.getDate() + (slot.offset === undefined ? offset : slot.offset))
      return `${d.getMonth() + 1}/${d.getDate()}`
    },

    resetForm () {
      this.prefillSubmitter()
      this.contactErrors = { name: '', email: '', phone: '' }
      this.products = [this.emptyProduct()]
      this.substitutions = []
      this.storeRows = [this.emptyStoreRow()]
      this.submitted = false
      this.submitOk = true
      this.submitMessage = ''
      this.requestReference = ''
      this.idempotencyKey = this.createIdempotencyKey()
      this.submissionUncertain = false
      this.retryPayload = null
    },

    // Close the success dialog, clear the form, and scroll to the top so the
    // next request starts from a clean slate (and can't be a resubmit)
    dismissSuccess () {
      this.showSuccess = false
      this.resetForm()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // Trims a product/substitution row to just the fields the API needs.
    productPayload (row) {
      return {
        cp_wine_id: row.cp_wine_id,
        producer_id: row.producer_id,
        supplier: row.supplier,
        item: row.item,
        cic: row.cic,
        upc: row.upc,
        demoCo: row.demoCo,
        regRetail: row.regRetail,
        tprRetail: row.tprRetail,
        is_new: row.isNew
      }
    },

    validateContact () {
      if (!this.groupContext) return true
      for (const field of ['name', 'email', 'phone']) {
        this.contactErrors[field] = (this.submittedBy[field] || '').trim() ? '' : `${field.charAt(0).toUpperCase() + field.slice(1)} is required.`
      }
      return Object.values(this.contactErrors).every(message => !message)
    },

    async submit () {
      if (!this.submissionUncertain && !this.validateContact()) {
        this.submitOk = false
        this.submitMessage = 'Enter your name, email and phone before submitting.'
        this.submitted = true
        return
      }
      // New items must have a name, brand, and UPC before we send them on
      const newRows = this.products.concat(this.substitutions).filter(r => r.isNew)
      const missing = newRows.some(r => !(r.item && r.item.trim()) || !(r.supplier && r.supplier.trim()) || !(r.upc && r.upc.trim()))
      if (missing && !this.submissionUncertain) {
        this.submitOk = false
        this.submitMessage = 'New items need a name, brand, and UPC before submitting.'
        this.submitted = true
        return
      }
      const payload = this.submissionUncertain && this.retryPayload ? this.retryPayload : {
        region: this.selectedRegion,
        submittedBy: this.submittedBy,
        products: this.products.map(this.productPayload),
        substitutions: this.substitutions.map(this.productPayload),
        stores: this.storeRows.map(r => ({
          costco_id: r.costco_id,
          store_num: r.store ? r.store.warehouse_id : null,
          city: r.city,
          address: r.address,
          week_start: r.weekStart,
          slots: Object.keys(r.slots).filter(k => r.slots[k])
        }))
      }
      this.retryPayload = JSON.parse(JSON.stringify(payload))
      this.submitting = true
      this.submissionUncertain = false

      try {
        await this.$store.dispatch('get_config')
        const url = `${this.$store.state.apiRoot}${this.requestBase}event-request`
        const response = await axios.post(url, payload, {
          headers: {
            Authorization: `Bearer ${this.requestToken}`,
            'Idempotency-Key': this.idempotencyKey
          }
        })
        const data = response.data || {}
        if (data.status === 'success') {
          this.requestReference = data.work_order || ''
          this.showSuccess = true
          this.$emit('request-complete')
        } else {
          this.submitOk = false
          this.submitMessage = data.message || 'Submission failed'
          this.submitted = true
        }
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : {}
        const status = error.response ? error.response.status : 0
        this.submitOk = false
        this.submissionUncertain = status === 0 || status >= 500
        this.submitMessage = this.submissionUncertain
          ? (data.message || 'We could not confirm whether the request was received. Do not clear the form; retry Submit to resend the original request safely.')
          : (data.message || 'Submission failed')
        this.submitted = true
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.required-mark {
  color: #c62828;
}
.store-slot-table .store-location {
  vertical-align: middle;
  padding-top: 16px;
}
.store-location .autofill {
  font-size: 16px;
  line-height: 1.5;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #10069F;
  margin: 0;
}
.page-subtitle {
  font-size: 13px;
  color: #888;
}

.section-card {
  border-radius: 6px;
}

.section-header {
  background: #10069F;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
}
.section-header--muted {
  background: #A773D7;
}

.table-scroll-container-x {
  overflow-x: auto;
}

.form-table {
  border-collapse: collapse;
  width: 100%;
  min-width: max-content;
  font-size: 13px;
}

.form-table thead th {
  background: #f5f5f5;
  border-bottom: 2px solid #d0d0d0;
  white-space: nowrap;
  padding: 6px 8px;
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #666;
}

.form-table th.req::after {
  content: ' *';
  color: #e53935;
}

.form-table td {
  padding: 2px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.item-col { min-width: 220px; }

/* New (off-catalog) items: a faint periwinkle tint so they read as special */
.new-item-row td { background: rgba(121, 120, 183, 0.09); }

.col-action {
  width: 36px;
  text-align: center;
}

/* Substitutions divider inside the shared Products table */
.subs-divider-row td {
  background: #eceef1;
  color: #555;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 6px 8px;
  border-bottom: 2px solid #d0d0d0;
}

.add-row-cell {
  padding: 4px 8px !important;
  border-bottom: none;
}

/* Store / slot table */
.store-slot-table th.store-col { min-width: 150px; }
.store-slot-table th.date-col  { min-width: 150px; }
.store-slot-table .slot-col {
  min-width: 64px;
  text-align: center;
  vertical-align: middle;
}
.slot-day  { font-size: 12px; font-weight: 700; }
.slot-time { font-size: 10px; opacity: 0.9; }

.slot-checkbox-cell {
  text-align: center;
}
.slot-checkbox {
  margin: 0 auto;
  justify-content: center;
  display: flex;
}
.slot-date {
  font-size: 10px;
  color: #888;
  text-align: center;
  margin-top: -2px;
}
.slot-date-booked {
  text-decoration: line-through;
  color: #c0392b;
}

.autofill {
  color: #444;
  font-size: 13px;
}

.slot-hint {
  font-size: 12px;
  color: #888;
  margin-bottom: 10px;
}

.add-btn {
  text-transform: none;
}

.downlink {
  font-size: 1rem;
}

.success-card {
  text-align: center;
}
.success-header {
  background: #10069F;
  color: #fff;
  padding: 26px 16px 22px;
}
.success-icon {
  font-size: 66px !important;
  color: #fff !important;
}
.success-title {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 8px 0 0;
}
.success-body {
  font-size: 16px;
  color: #444;
  line-height: 1.55;
  max-width: 460px;
  margin: 0 auto;
}
.success-reference {
  margin: 16px 0 0;
  color: #10069F;
  font-size: 15px;
}

b, strong {
  font-size: 14px !important;
}
</style>
